import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, ArrowRight, ShieldCheck, Wrench, Bug, Sparkles, X } from 'lucide-react';
import { useChangelogHighlights, ChangelogEntry } from '../../hooks/useTerms';
import { useAuthStore } from '../../stores/authStore';

/**
 * Zvonek s oznámeními v hlavičce postranního menu.
 *
 * Ukazuje jen novinky, které poskytovatel označil jako zásadní a mají ještě
 * nevypršený počet dní (filtruje server). Dokud je uživatel neotevře, svítí
 * u zvonku počet; po otevření se zvonek zklidní, ale oznámení v seznamu
 * zůstanou, dokud jejich okno neuplyne.
 *
 * Přečtení si drží prohlížeč zvlášť pro každého uživatele – na sdíleném
 * počítači by jinak jeden člověk „odklikl" oznámení všem. Klíčem je datum
 * a nadpis, protože publikace novinek přepisuje tabulku i s id.
 */

function readKey(userId?: string | null): string { return `news_read:${userId || 'anon'}`; }
function entryKey(e: { date: string; title: string }): string { return `${e.date}|${e.title}`; }

function loadRead(userId?: string | null): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(readKey(userId)) || '[]');
    return Array.isArray(raw) ? raw.filter(x => typeof x === 'string') : [];
  } catch { return []; }
}
function saveRead(userId: string | null | undefined, keys: string[]) {
  try { localStorage.setItem(readKey(userId), JSON.stringify(keys.slice(-50))); }
  catch { /* soukromé okno / plné úložiště */ }
}

const STYLE: Record<string, { label: string; Icon: typeof Sparkles; ring: string; text: string }> = {
  novinka:    { label: 'Novinka',    Icon: Sparkles,    ring: 'bg-blue-100',    text: 'text-blue-700'    },
  vylepseni:  { label: 'Vylepšení',  Icon: Wrench,      ring: 'bg-violet-100',  text: 'text-violet-700'  },
  bezpecnost: { label: 'Bezpečnost', Icon: ShieldCheck, ring: 'bg-emerald-100', text: 'text-emerald-700' },
  oprava:     { label: 'Oprava',     Icon: Bug,         ring: 'bg-amber-100',   text: 'text-amber-700'   },
};

export default function NewsBell({ collapsed = false }: { collapsed?: boolean }) {
  const { data } = useChangelogHighlights();
  const navigate = useNavigate();
  const userId = useAuthStore(st => st.user?.id ?? null);

  const [open, setOpen] = useState(false);
  const [read, setRead] = useState<string[]>(() => loadRead(userId));
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const items: ChangelogEntry[] = data ?? [];
  const unread = items.filter(e => !read.includes(entryKey(e))).length;

  // Zavření kliknutím mimo panel a klávesou Esc
  useEffect(() => {
    if (!open) return;
    const onClick = (ev: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(ev.target as Node)) setOpen(false);
    };
    const onEsc = (ev: KeyboardEvent) => { if (ev.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onClick); document.removeEventListener('keydown', onEsc); };
  }, [open]);

  // Ve sbalenem menu neni misto – zvonek by vykukoval ven přes okraj panelu,
  // takže se schová úplně. Po rozbalení je zase vidět i s počítadlem.
  if (collapsed || !items.length) return null;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && unread) {
      const keys = Array.from(new Set([...read, ...items.map(entryKey)]));
      setRead(keys);
      saveRead(userId, keys);
    }
  };

  return (
    <div ref={wrapRef} className="relative ml-auto shrink-0">
      <button onClick={toggle} title="Novinky v systému"
              className="app-sidebar-text app-sidebar-hover relative p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors">
        <Bell className="h-5 w-5"/>
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white
                           text-[10px] font-bold leading-4 text-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-2 w-[340px] max-h-[70vh] overflow-y-auto z-50
                        bg-white text-gray-900 rounded-xl shadow-2xl border border-gray-200">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 sticky top-0 bg-white rounded-t-xl">
            <Bell className="h-4 w-4 text-blue-600"/>
            <span className="font-semibold text-sm">Novinky v systému</span>
            <button onClick={() => setOpen(false)} className="ml-auto text-gray-400 hover:text-gray-700">
              <X className="h-4 w-4"/>
            </button>
          </div>

          <div className="divide-y divide-gray-100">
            {items.map(e => {
              const st = STYLE[e.category] ?? STYLE.novinka;
              return (
                <div key={e.id} className="px-4 py-3">
                  <div className="flex items-start gap-2.5">
                    <span className={`mt-0.5 h-7 w-7 rounded-lg flex items-center justify-center shrink-0 ${st.ring}`}>
                      <st.Icon className={`h-3.5 w-3.5 ${st.text}`}/>
                    </span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-[11px] font-semibold uppercase tracking-wide ${st.text}`}>{st.label}</span>
                        <span className="text-[11px] text-gray-400">
                          {new Date(e.date + 'T00:00:00').toLocaleDateString('cs-CZ')}
                        </span>
                      </div>
                      <div className="font-medium text-sm mt-0.5">{e.title}</div>
                      {e.body && <p className="text-xs text-gray-600 mt-1 leading-relaxed">{e.body}</p>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <button onClick={() => { setOpen(false); navigate('/settings'); }}
                  className="w-full px-4 py-3 border-t border-gray-100 text-xs font-medium text-blue-700
                             hover:bg-blue-50 flex items-center justify-center gap-1 rounded-b-xl">
            Všechny novinky a aktualizace <ArrowRight className="h-3.5 w-3.5"/>
          </button>
        </div>
      )}
    </div>
  );
}
